import React, { useState, useEffect, useRef } from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Box, LinearProgress, List, ListItem, ListItemText, ListItemAvatar, Avatar } from "@material-ui/core";
import { PlainTextField, PasswordTextField } from "components";
import { getAuthClubMembers } from "api/auth";
import { ReducedUserProfileWithEmail } from "api/user";
import i18n from "../i18n";
import styles from "./LoginDialog.module.scss";

// prop types
interface LoginDialogProps {
    error: string | null,
    isDashboardMode: boolean,
    isLoading: boolean,
    showDialog: boolean,
    onClose: () => void,
    onForgotPassword: () => void,
    onLoginUser: (email: string, password: string) => void,
    onSignUp: () => void,
}

// login dialog
const LoginDialog = ({ error, isDashboardMode, isLoading, showDialog, onClose, onForgotPassword, onLoginUser, onSignUp }: LoginDialogProps) => {
    // ui state
    const [clubMembers, setClubMembers] = useState<ReducedUserProfileWithEmail[]>([]);
    const [emailAddress, setEmailAddress] = useState("");
    const [obfEmailAddress, setObfEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const passwordInputRef = useRef<HTMLInputElement>(null);

    // did hide hook -> clear input fields
    useEffect(() => {
        if (!showDialog) {
            setClubMembers([]);
            setEmailAddress("");
            setObfEmailAddress("");
            setPassword("");
        }
    }, [showDialog]);

    // email address field change handler
    const onChangeEmailAddress = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        setEmailAddress(input);

        // reset email if easy login was selected before
        if (obfEmailAddress) {
            setObfEmailAddress("");
            setEmailAddress(input.slice(-1)); // keep newly entered key
        }

        // fetch club members for easy login
        if (isDashboardMode && input.length > 2) {
            setClubMembers(await getAuthClubMembers(input));
        } else {
            setClubMembers([]);
        }
    };

    // obfuscate email for easy login
    const obfuscateEmail = (email: string) => `${email.slice(0, 3)}***${email.slice(-3)}`;

    // easy login member selected
    const onClubMemberSelected = ({ email }: ReducedUserProfileWithEmail) => {
        setEmailAddress(email);
        setObfEmailAddress(obfuscateEmail(email));
        setClubMembers([]);
        passwordInputRef.current!.focus();
    };

    // relevant errors
    const relevantErrors = ["AuthError", "InvalidParameterException", "NotAuthorizedException", "UserNotFoundException"];

    // render
    return (
        <Dialog
            open={showDialog}
            onClose={onClose}
        >
            <DialogTitle>{i18n.signIn}</DialogTitle>
            <DialogContent dividers>

                {/* club members list for easy login */}
                {(clubMembers.length > 0) && (
                    <List>
                        {clubMembers.map((member) => (
                            <ListItem
                                button
                                key={member.id}
                                onClick={() => onClubMemberSelected(member)}
                            >
                                <ListItemAvatar>
                                    <Avatar src={member.profilePictureUrl} />
                                </ListItemAvatar>
                                <ListItemText
                                    primary={`${member.firstName} ${member.lastName}`}
                                    secondary={obfuscateEmail(member.email)}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

                {/* email */}
                <PlainTextField
                    autoFocus
                    disabled={isLoading}
                    error={relevantErrors.includes(error!)}
                    label={i18n.emailAddress}
                    type="email"
                    value={obfEmailAddress || emailAddress}
                    onChange={onChangeEmailAddress}
                />

                {/* password */}
                <PasswordTextField
                    disabled={isLoading}
                    error={relevantErrors.includes(error!)}
                    helperText={relevantErrors.includes(error!) && i18n.errors[error!]}
                    inputRef={passwordInputRef}
                    label={i18n.password}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => (e.key === "Enter") && onLoginUser(emailAddress, password)}
                />

                {/* forgot password */}
                <Box className={styles.forgotPasswordBox}>
                    <Button onClick={onForgotPassword}>
                        {i18n.forgotPassword}
                    </Button>
                </Box>

            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onSignUp} style={{ marginRight: "auto" }}>
                    {i18n.signUp}
                </Button>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" onClick={() => onLoginUser(emailAddress, password)} disabled={isLoading}>
                    {i18n.signIn}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LoginDialog;
